/**
 * FACT-CHECK AUTHORITY LOOKUP — the deterministic half of checking a legal rule
 * an article states WITHOUT citing anything.
 *
 * "The initial evidentiary burden falls entirely on the taxpayer" has no section
 * number, so citation-led extraction never sees it. Texas Tax Code § 41.43(a)
 * puts that burden on the appraisal district. This module does two things, both
 * by lexicon and both in code (the model never decides what is a legal assertion
 * or which provision to read):
 *
 *   1. `uncitedRuleStrength` — does a sentence state a legal rule (burden of proof,
 *      who must / shall, a filing deadline, a penalty, an exemption, an appeal
 *      right)? Only sentences that name a legal actor qualify, so ordinary prose
 *      with the word "must" does not.
 *   2. `authorityRefsFor` — which provisions a bounded, static map of common Texas
 *      property-tax topics points to. The map only chooses WHAT TO READ. A wrong
 *      entry costs a fetch and yields "could not be verified"; it can never turn
 *      into a verdict, because the verdict still rests on a quote from the page.
 *
 * Pure: no I/O, no model, no clock.
 */

export interface AuthorityRef {
  /** Legislature code abbreviation (TX, GV...). */
  code: string;
  section: string;
}

interface AuthorityTopic {
  id: string;
  /** Matches the sentence's own words. */
  test: RegExp;
  refs: readonly AuthorityRef[];
}

const tx = (...sections: string[]): AuthorityRef[] =>
  sections.map((section) => ({ code: 'TX', section }));

/**
 * Common Texas property-tax topics → the provisions that govern them. Ordered by
 * specificity; a sentence takes every topic it matches (capped by the caller).
 */
export const AUTHORITY_TOPICS: readonly AuthorityTopic[] = [
  {
    id: 'burden-of-proof',
    test: /\b(?:burden of (?:proof|persuasion|production)|evidentiary burden|(?:bears?|has|carr(?:y|ies)|shoulders?) the (?:initial |evidentiary )?burden|burden (?:falls|rests|lies|shifts))\b/i,
    refs: tx('41.43'),
  },
  {
    id: 'unequal-appraisal',
    test: /\b(?:unequal appraisal|equal and uniform|median level of appraisal)\b/i,
    refs: tx('41.43', '42.26'),
  },
  {
    id: 'protest-deadline',
    test: /\b(?:protest(?:s|ed|ing)?\b[^.]{0,80}\b(?:deadline|within|before|no later than|not later than|by)\b|(?:deadline|within|before|no later than|not later than)\b[^.]{0,80}\bprotest|notice of protest|may 15)\b/i,
    refs: tx('41.44'),
  },
  {
    id: 'rendition',
    test: /\b(?:render(?:ed|ing|s)?|renditions?)\b/i,
    refs: tx('22.01', '22.23'),
  },
  {
    id: 'tax-lien',
    test: /\b(?:(?:property )?tax liens?|lien attaches|liens? (?:on|attach))\b/i,
    refs: tx('32.01'),
  },
  {
    id: 'appraisal-date',
    test: /\b(?:apprais(?:ed|al)\b[^.]{0,80}\bJanuary 1|January 1\b[^.]{0,80}\bapprais(?:ed|al)|appraisal date)\b/i,
    refs: tx('23.01'),
  },
  {
    id: 'rendition-penalty',
    test: /\b(?:penalt(?:y|ies)|late fee)\b[^.]{0,80}\b(?:render|rendition|fil(?:e|ing)|report)|\b(?:render|rendition|fil(?:e|ing)|report)\b[^.]{0,80}\bpenalt(?:y|ies)\b/i,
    refs: tx('22.28', '22.30'),
  },
  {
    id: 'delinquent-penalty',
    test: /\b(?:delinquen(?:t|cy)|late payment|interest (?:accrues|on unpaid))\b/i,
    refs: tx('33.01'),
  },
  {
    id: 'cost-method',
    test: /\bcost (?:method|approach)\b|\breplacement cost new\b|\bRCNLD\b/i,
    refs: tx('23.011'),
  },
  {
    id: 'market-value',
    test: /\bmarket value\b/i,
    refs: tx('23.01'),
  },
  {
    id: 'appeal-to-court',
    test: /\b(?:judicial review|(?:appeal|appeals|appealed|appealing)\b[^.]{0,60}\b(?:district )?court|(?:district )?court\b[^.]{0,60}\b(?:appeal|petition|review)|petition for review)\b/i,
    refs: tx('42.01', '42.21'),
  },
  {
    id: 'right-of-appeal',
    test: /\b(?:right to appeal|entitled to appeal|(?:may|can) appeal)\b/i,
    refs: tx('42.01'),
  },
  {
    id: 'arb',
    test: /\b(?:[Aa]ppraisal [Rr]eview [Bb]oards?|ARBs?)\b/,
    refs: tx('41.01', '41.41'),
  },
  {
    id: 'right-of-protest',
    test: /\b(?:right to protest|entitled to protest|(?:may|can) protest)\b/i,
    refs: tx('41.41'),
  },
  {
    id: 'arbitration',
    test: /\bbinding arbitration\b/i,
    refs: tx('41A.01'),
  },
  {
    id: 'homestead',
    test: /\bhomestead exemption\b/i,
    refs: tx('11.13'),
  },
  {
    id: 'freeport',
    test: /\bfreeport\b/i,
    refs: tx('11.251'),
  },
  {
    id: 'abatement',
    test: /\b(?:tax )?abatements?\b/i,
    refs: tx('312.002'),
  },
  {
    id: 'data-center-sales-tax',
    test: /\bdata cent(?:er|re)s?\b[^.]{0,120}\b(?:sales|use) tax|\b(?:sales|use) tax\b[^.]{0,120}\bdata cent(?:er|re)s?\b/i,
    refs: tx('151.359', '151.3595'),
  },
  {
    id: 'data-center-exemption',
    test: /\b(?:qualifying data cent(?:er|re)s?|large data cent(?:er|re) projects?|data cent(?:er|re) (?:exemption|project))\b/i,
    refs: tx('151.359', '151.3595'),
  },
  {
    id: 'jeti',
    test: /\b(?:JETI|Jobs,? Energy,? Technology,? (?:and|&) Innovation)\b/i,
    refs: [
      { code: 'GV', section: '403.602' },
      { code: 'GV', section: '403.607' },
    ],
  },
];

/** The most distinct provisions one sentence may send to the fetcher. */
export const MAX_AUTHORITY_REFS_PER_CLAIM = 4;

export function sectionChapter(section: string): string {
  return section.split('.')[0]!;
}

/** Provisions the topic map points a sentence to, de-duplicated, in map order. */
export function authorityRefsFor(
  sentence: string,
  max: number = MAX_AUTHORITY_REFS_PER_CLAIM,
): AuthorityRef[] {
  const found: AuthorityRef[] = [];
  const seen = new Set<string>();
  for (const topic of AUTHORITY_TOPICS) {
    if (!topic.test.test(sentence)) continue;
    for (const ref of topic.refs) {
      const key = `${ref.code}.${ref.section}`;
      if (seen.has(key)) continue;
      seen.add(key);
      found.push(ref);
    }
  }
  return found.slice(0, max);
}

interface Companion {
  /** `CODE.section` already cited. */
  cited: string;
  /** Only when the claim's own words reach for what the cited section does not hold. */
  when: RegExp;
  refs: readonly AuthorityRef[];
}

/**
 * A cited section often covers one half of a compound claim and a neighbour
 * covers the other. § 42.01 grants the right to appeal an ARB order; § 42.21 says
 * the petition goes to the district court and when. A claim that names the forum
 * or the time limit but cites only § 42.01 would otherwise read as "not stated"
 * about a provision that never claimed to say it.
 */
export const COMPANIONS: readonly Companion[] = [
  {
    cited: 'TX.42.01',
    when: /\b(?:district court|petition|within \d+|\d+ days|deadline|jurisdiction|venue)\b/i,
    refs: tx('42.21'),
  },
  {
    cited: 'TX.41.41',
    when: /\b(?:deadline|within \d+|\d+ days|no later than|not later than|may 15)\b/i,
    refs: tx('41.44'),
  },
];

export function companionRefsFor(citedKeys: readonly string[], claimText: string): AuthorityRef[] {
  const out: AuthorityRef[] = [];
  const cited = new Set(citedKeys);
  for (const companion of COMPANIONS) {
    if (!cited.has(companion.cited) || !companion.when.test(claimText)) continue;
    for (const ref of companion.refs) {
      const key = `${ref.code}.${ref.section}`;
      if (!cited.has(key) && !out.some((item) => `${item.code}.${item.section}` === key))
        out.push(ref);
    }
  }
  return out;
}

// ── Does a sentence state a legal rule? ────────────────────────────────────

const BURDEN = AUTHORITY_TOPICS[0]!.test;
/** Someone the law binds or protects. Plain prose with "must" does not name one. */
const LEGAL_ACTOR =
  /\b(?:taxpayers?|property owners?|owners? of (?:the )?property|chief appraisers?|appraisal (?:district|review board)s?|ARBs?|assessors?|comptroller|taxing units?|tax (?:assessor|collector)s?|district court|appraisers?)\b/i;
const OBLIGATION =
  /\b(?:must|shall|is required to|are required to|required by (?:law|statute)|is obligated to|are obligated to|legally (?:required|obligated)|has a duty|have a duty)\b/i;
const PENALTY_WORD = /\b(?:penalt(?:y|ies)|forfeit(?:s|ed|ure)?|fines?)\b/i;
const EXEMPTION_WORD = /\b(?:exempt(?:ion|ions|ed)?|abatements?)\b/i;
const APPEAL_RIGHT =
  /\b(?:right to (?:appeal|protest)|entitled to (?:appeal|protest)|(?:may|can|must) (?:appeal|protest)|appeals? to (?:the |a )?(?:district )?court|judicial review)\b/i;
const DEADLINE_WITH_DATE = new RegExp(
  '\\b(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|June?|July?|Aug(?:ust)?|Sept?(?:ember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\\.?\\s+\\d{1,2}\\b|\\b\\d{1,3}[\\s-](?:calendar\\s+|business\\s+)?(?:days?|weeks?|months?)\\b',
  'i',
);
const FILING_WORD =
  /\b(?:fil(?:e|ed|ing)|protest|appeal|render|rendition|notice|pay(?:ment)?|deadline|submit|request)\b/i;

/**
 * 0 = not a legal-rule sentence. Higher = a stronger candidate when the cap bites:
 * the burden of proof outranks a deadline with a date, which outranks a bare
 * "must".
 */
export function uncitedRuleStrength(sentence: string): number {
  if (sentence.length < 25) return 0;
  if (BURDEN.test(sentence)) return 4;
  if (!LEGAL_ACTOR.test(sentence)) return 0;
  if (DEADLINE_WITH_DATE.test(sentence) && FILING_WORD.test(sentence)) return 3;
  if (APPEAL_RIGHT.test(sentence) || PENALTY_WORD.test(sentence) || EXEMPTION_WORD.test(sentence))
    return 2;
  if (OBLIGATION.test(sentence)) return 1;
  return 0;
}

// ── Where the article stands ───────────────────────────────────────────────

/**
 * A bare "Section 23.011" is a Tax Code citation only in an article about Texas
 * property tax. There is no workspace context in a claim check job, so the
 * article's own text decides: it names the Texas Tax Code, or it is about Texas
 * property tax by its own vocabulary.
 */
export function inferDefaultCode(markdown: string): string | null {
  if (/\bTex(?:as|\.)?\s+(?:Property\s+)?Tax\s+Code\b/i.test(markdown)) return 'TX';
  if (
    /\bTexas\b/i.test(markdown) &&
    /\b(?:property tax(?:es)?|ad valorem|appraisal (?:district|review board)|chief appraiser|ARB)\b/i.test(
      markdown,
    )
  )
    return 'TX';
  return null;
}

/**
 * "Section 3.2 of the lease" must never be read as a statute, even in a Texas
 * tax article.
 */
export const NON_STATUTE_TAIL =
  /^\s*(?:\([a-z0-9]+\)\s*)*(?:of|in)\s+(?:the|this|that|our|your|a|an)\s+(?:\w+\s+){0,2}?(?:lease|agreement|contract|policy|plan|report|memo|memorandum|ordinance|bylaws?|handbook|engagement|statement of work|SOW|terms|invoice|proposal|document)\b/i;

/** Tax Code section numbers: a chapter of one to three digits, then at least two decimal digits. */
export function looksLikeTaxCodeSection(section: string): boolean {
  const match = /^(\d{1,3})[A-Za-z]?\.(\d{2,})[A-Za-z]?$/.exec(section);
  return Boolean(match) && Number(match![1]) <= 313;
}

const SCOPE_TERMS: ReadonlyArray<[string, RegExp]> = [
  ['real property', /\breal (?:property|estate)\b/i],
  ['personal property', /\b(?:business )?personal property\b|\bBPP\b/i],
  [
    'machinery and equipment (often personal property)',
    /\b(?:industrial )?(?:machinery|equipment)\b/i,
  ],
  ['industrial property', /\bindustrial\b/i],
  ['commercial property', /\bcommercial\b/i],
  ['residential property', /\bresidential\b|\bhomestead\b/i],
  ['inventory', /\binventory\b/i],
  ['data centers', /\bdata cent(?:er|re)s?\b/i],
];

/** What kind of property the article as a whole is about, so applicability can be judged. */
export function articleScopeTerms(markdown: string): string[] {
  return SCOPE_TERMS.filter(([, pattern]) => pattern.test(markdown)).map(([label]) => label);
}
